import { NotificationTypeDto, type NotificationDto } from "../../autogen/bff-api";
import { urls } from "../../urls";
import {
  requireBidMeta,
  requireContractDocumentMeta,
  requireContractReminderMeta,
  requireContractkMeta,
  requireSeV2DoffinPublicationFailedMeta,
  requireSeV2DoffinPublishedMeta,
  requireSeV2ResponsibleBuyerChangedMeta,
  requireSourcingEventAnnouncementMeta,
  requireSourcingEventMeta,
  requireSourcingEventQuestionCommentMeta,
  requireSourcingEventQuestionMeta,
  requireSeV2InvitedToTenderMeta,
  requireSeV2OrganizationInviteMeta,
  requireSeV2DpsAdmittedMeta,
  requireSeV2DpsRemovedMeta,
  requireSeV2RequestNotQualifiedMeta,
  requireSeV2RequestNotSelectedMeta,
  requireTaskMeta,
} from "./get-notification-description";

export const getNotificationLink = (notification: NotificationDto, selectedOrgId: string): string | undefined => {
  switch (notification.type) {
    case NotificationTypeDto.ContractDateReminder: {
      const meta = requireContractReminderMeta(notification);
      return urls.contracts.viewSingle.go(meta.contractId);
    }
    case NotificationTypeDto.ContractDocumentCompletedSigning: {
      const meta = requireContractDocumentMeta(notification);
      return urls.contracts.viewSingle.documents(meta.contractId);
    }
    case NotificationTypeDto.ContractSignatureDeadlineExpired: {
      const meta = requireContractDocumentMeta(notification);
      return urls.contracts.viewSingle.documents(meta.contractId);
    }
    case NotificationTypeDto.ContractSharedWithDepartment:
    case NotificationTypeDto.ContractSharedWithOrg:
    case NotificationTypeDto.ContractSharedWithPerson: {
      const meta = requireContractkMeta(notification);
      return urls.contracts.viewSingle.go(meta.contractId);
    }
    case NotificationTypeDto.ContractMessageReceived: {
      const meta = requireContractkMeta(notification);
      return urls.contracts.viewSingle.dialog(meta.contractId);
    }
    case NotificationTypeDto.ContractTaskCommentAdded:
    case NotificationTypeDto.ContractTaskDeleted:
    case NotificationTypeDto.ContractTaskDueDateUpdated:
    case NotificationTypeDto.ContractTaskDelegated:
    case NotificationTypeDto.ContractTaskStatusUpdated:
    case NotificationTypeDto.ContractTaskDueDateReminder: {
      const meta = requireTaskMeta(notification);
      return urls.contracts.viewSingle.task(meta.contractId, meta.taskId);
    }
    case NotificationTypeDto.SourcingEventAnnouncement: {
      const meta = requireSourcingEventAnnouncementMeta(notification);
      return urls.events.supplierView.announcement(meta.eventId, meta.announcementId);
    }
    case NotificationTypeDto.SourcingEventAwarded:
    case NotificationTypeDto.BidDeliveryDeadline:
    case NotificationTypeDto.SourcingEventOrganizationInvite:
    case NotificationTypeDto.SourcingEventCancelled:
    case NotificationTypeDto.SourcingEventClosed:
    case NotificationTypeDto.NegotiationInvite: {
      const meta = requireSourcingEventMeta(notification);
      if (notification.meta.sourcingEvent?.eventOwningOrganizationId === selectedOrgId) {
        return urls.events.view.go(meta.eventId);
      }
      return urls.events.supplierView.go({
        eventId: meta.eventId,
        negotiationRoundId: notification.meta.negotiationRoundId,
      });
    }
    case NotificationTypeDto.BidDelivered: {
      const meta = requireBidMeta(notification);
      return urls.events.view.go(meta.eventId);
    }
    case NotificationTypeDto.SourcingEventNewQuestion: {
      const meta = requireSourcingEventQuestionMeta(notification);
      return meta.receiverRole === "Buyer"
        ? urls.events.view.question(meta.eventId, meta.questionId)
        : urls.events.supplierView.question(meta.eventId, meta.questionId);
    }
    case NotificationTypeDto.SourcingEventNewQuestionComment: {
      const meta = requireSourcingEventQuestionCommentMeta(notification);
      return meta.receiverRole === "Buyer"
        ? urls.events.view.question(meta.eventId, meta.questionId)
        : urls.events.supplierView.question(meta.eventId, meta.questionId);
    }
    case NotificationTypeDto.SourcingEventQuestionStatusUpdated: {
      const meta = requireSourcingEventQuestionMeta(notification);
      return meta.receiverRole === "Buyer"
        ? urls.events.view.question(meta.eventId, meta.questionId)
        : urls.events.supplierView.question(meta.eventId, meta.questionId);
    }
    case NotificationTypeDto.SeV2NewQuestion:
    case NotificationTypeDto.SeV2QuestionAnswered:
    case NotificationTypeDto.SeV2ClarificationMessageToBuyer:
    case NotificationTypeDto.SeV2ClarificationMessageToSupplier: {
      const meta = requireSourcingEventQuestionMeta(notification);
      return meta.receiverRole === "Buyer"
        ? urls.v2.sourcingEvents.view.question(meta.eventId, meta.questionId)
        : urls.portal.view.question(meta.eventId, meta.questionId);
    }
    case NotificationTypeDto.SeV2InformationPublished: {
      const meta = requireSourcingEventAnnouncementMeta(notification);
      return urls.portal.view.announcement(meta.eventId, meta.announcementId);
    }
    case NotificationTypeDto.SeV2BidDelivered: {
      const meta = requireSourcingEventMeta(notification);
      return `${urls.v2.sourcingEvents.view.go(meta.eventId)}?tab=bids`;
    }
    case NotificationTypeDto.SeV2AwardLetterDispatched:
    case NotificationTypeDto.SeV2AwardDecisionRevoked:
    case NotificationTypeDto.SeV2BidDeadlineReminder:
    case NotificationTypeDto.SeV2EventCancelled:
    case NotificationTypeDto.SeV2EventClosed:
    case NotificationTypeDto.SeV2RoundPublished:
    case NotificationTypeDto.SeV2EventChanged: {
      const meta = requireSourcingEventMeta(notification);
      return urls.portal.view.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2InvitedToTender: {
      const meta = requireSeV2InvitedToTenderMeta(notification);
      return urls.portal.view.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2OrganizationInvite: {
      const meta = requireSeV2OrganizationInviteMeta(notification);
      return urls.portal.view.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2RequestNotQualified: {
      const meta = requireSeV2RequestNotQualifiedMeta(notification);
      return urls.portal.view.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2RequestNotSelected: {
      const meta = requireSeV2RequestNotSelectedMeta(notification);
      return urls.portal.view.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2DpsAdmitted: {
      const meta = requireSeV2DpsAdmittedMeta(notification);
      return urls.portal.view.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2DpsRemoved: {
      const meta = requireSeV2DpsRemovedMeta(notification);
      return urls.portal.view.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2ResponsibleBuyerChanged: {
      const meta = requireSeV2ResponsibleBuyerChangedMeta(notification);
      return urls.v2.sourcingEvents.view.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2DoffinPublished: {
      const meta = requireSeV2DoffinPublishedMeta(notification);
      return urls.v2.sourcingEvents.view.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2DoffinPublicationFailed: {
      const meta = requireSeV2DoffinPublicationFailedMeta(notification);
      return urls.v2.sourcingEvents.view.go(meta.eventId);
    }
  }
};
